import * as React from 'react';
import { AdvancedPlansFeature, AdvancedPlansProductData } from 'types';
import s from './PlansGrid.scss';
import groupStyle from './Group.scss';
import Check from 'wix-ui-icons-common/Check';
import HelpCircle from 'wix-ui-icons-common/HelpCircle';
import MinusSmall from 'wix-ui-icons-common/MinusSmall';
import classNames from 'classnames';
import { Text, Badge } from '@wix/design-system';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import { withInjectedRootStore, RootStore } from 'providers';
import { Trans, useTranslation } from 'react-i18next';
import { PaymentCycle_BIMap } from 'services/BiLogger/BiLoggerTypes';
import { useDelayedTooltipBiEvent } from '../../../../hooks/useDelayedTooltipBiEvent';
import { Tooltip } from 'components/Tooltip';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';
import { getAdVoucherTransParams, getCountryCodeTld } from 'utils';
import { TruncatedTextWrapper } from 'components/TruncatedTextTooltip';
import { TooltipLink } from 'components/TooltipLink';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { useBreakpoint } from 'providers/BreakpointProvider';
import { Experiments, MediaDevices, Theme } from 'enums';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { featureRowDataHooks as dataHooks } from './FeatureRow.datahooks';

interface FeatureRowProps extends InjectedExperimentsProps {
  products: AdvancedPlansProductData[];
  feature: AdvancedPlansFeature;
  rootStore: RootStore;
  className?: string;
  cellClassName?: string;
  style?: React.CSSProperties;
}

export const FeatureRowComp = React.memo<FeatureRowProps>(
  ({
    products,
    feature,
    rootStore,
    className,
    style,
    cellClassName,
    experiments,
  }) => {
    const { appStore } = rootStore;
    const { theme } = usePageModeContext();
    const isDayfullTheme = theme === Theme.Dayful;
    const {
      currentCycle,
      appContext: { biLogger, requestParamsMap, country },
      isJapanesePackagePicker,
      isInSale,
      isSunriseV2Enabled,
    } = appStore;
    const { t } = useTranslation();

    const { currentBreakpoint } = useBreakpoint();

    const tooltipsAsASubtitle =
      isJapanesePackagePicker && currentBreakpoint === MediaDevices.Desktop;

    const { amountToSpend, amount, currencyCode } =
      appStore.googleVoucherAmounts;
    const [currencyToString] = usePremiumCurrencyFormatter({
      currencyCode,
    });
    const voucherTransParams = getAdVoucherTransParams(
      currencyToString,
      amount,
      amountToSpend,
    );
    const domainFeatureTransParams = {
      domain: requestParamsMap.domainName,
      domainName: requestParamsMap.domainName,
      ccTLD: `${getCountryCodeTld(country)}, .co.uk`, // for tooltip
    };

    const transParams = { ...voucherTransParams, ...domainFeatureTransParams };

    const featureValue = React.useCallback((productId) => {
      if (!feature.value || (feature.value && !feature.value.get(productId))) {
        return <Check />;
      }
      const key = feature.value.get(productId);
      return key && t(key);
    }, []);

    const onTooltipViewed = () =>
      biLogger.logViewProductFeatureTooltip({
        cycle: PaymentCycle_BIMap[currentCycle],
        fromStep: 'select_plan',
        productId: 0,
        featureKey: feature.translationKey,
      });
    const { logShowTooltipDelayedEvent, interuptTooltipDelayedEvent } =
      useDelayedTooltipBiEvent(onTooltipViewed);
    const _products: [string, boolean][] = React.useMemo(
      () =>
        products.map((p) => [
          p.id.toString(),
          new AdvancedPlansProductModel(p, appStore).isSelected(),
        ]),
      [products],
    );
    const featureExistsInProducts = _products
      .map(([id]) => feature.includedInProducts.has(id))
      .some((v) => v);
    if (!featureExistsInProducts) {
      return null;
    }
    const translationTags = {
      b: <b />,
      ul: <ul />,
      li: <li />,
      div: (
        <div data-hook={dataHooks.voucherDisclaimer} className={s.adVouchers} />
      ),
      ellipsis: (
        <TruncatedTextWrapper
          dataHook={dataHooks.truncatedDomainName}
          text={requestParamsMap.domainName}
          maxWidth={136}
          maxTextLength={17}
          componentProps={{
            weight: 'bold',
            size: 'small',
          }}
        />
      ),
      url: (
        <TooltipLink
          feature={feature}
          onClick={biLogger.logClickLinkInProductFeatureTooltip}
        />
      ),
    };

    const showNewBadge =
      isSunriseV2Enabled &&
      experiments.enabled(Experiments.NewFeatureBadge) &&
      feature.displayProperties?.hasNewBadge;
    const compactMargin = isSunriseV2Enabled;

    return (
      <div className={classNames(s.gridRow, className)} style={style}>
        <div
          className={classNames(s.cell, s.featureName, cellClassName)}
          data-testid={dataHooks.name}
          data-is-ad-voucher={feature.isGoogleVoucher}
          key={feature.translationKey}
        >
          <div
            className={classNames(s.featureName, {
              [groupStyle.featureName]: !compactMargin,
            })}
          >
            <div className={classNames(s.nameAndBadge)}>
              <Text
                size='small'
                weight={isJapanesePackagePicker ? 'bold' : 'normal'}
              >
                <Trans
                  i18nKey={feature.translationKey}
                  components={translationTags}
                  values={transParams}
                />
              </Text>
              {showNewBadge && (
                <Badge
                  skin='neutralSuccess'
                  dataHook={dataHooks.newBadge}
                  size='tiny'
                  className={s.newBadge}
                >
                  {t('features.new_feature.new_badge')}
                </Badge>
              )}
            </div>

            {!tooltipsAsASubtitle && feature.benefit && (
              <Text size='tiny' secondary dataHook={dataHooks.benefit}>
                {t(feature.benefit)}
              </Text>
            )}

            {tooltipsAsASubtitle && (
              <div>
                <Text
                  size='tiny'
                  data-testid={dataHooks.description}
                  className={s.descriptionJapan}
                  secondary={isJapanesePackagePicker}
                >
                  <Trans
                    i18nKey={feature.tooltipTranslationKey}
                    components={translationTags}
                    values={transParams}
                  />
                </Text>
                {feature.benefit && (
                  <Text size='tiny' secondary dataHook={dataHooks.benefit}>
                    {' ' + t(feature.benefit)}
                  </Text>
                )}
              </div>
            )}
          </div>
          {!tooltipsAsASubtitle && (
            <Tooltip
              dataHook={dataHooks.tooltip}
              maxWidth={300}
              placement='right'
              onOpen={logShowTooltipDelayedEvent}
              onClose={interuptTooltipDelayedEvent}
              content={
                <Trans
                  i18nKey={feature.tooltipTranslationKey}
                  components={translationTags}
                  values={transParams}
                >
                  <Text size='small'>{feature.tooltipTranslationKey}</Text>
                </Trans>
              }
            >
              {isJapanesePackagePicker ? (
                <HelpCircle />
              ) : (
                <i className={s.noteIcon} />
              )}
            </Tooltip>
          )}
        </div>
        {_products.map(([id, isPreselected], idx) =>
          feature.includedInProducts.has(id.toString()) ? (
            <div
              key={dataHooks.vIcon(idx)}
              data-testid={dataHooks.vIcon(idx)}
              className={classNames(s.cell, theme, cellClassName, {
                [s.preselected]: !isSunriseV2Enabled && isPreselected,
                [s.circledCheckmark]: isJapanesePackagePicker || isDayfullTheme,
                [s.sale]: isInSale,
              })}
            >
              <Text size='small'>{featureValue(id)}</Text>
            </div>
          ) : (
            <div
              key={dataHooks.xIcon(idx)}
              data-testid={dataHooks.xIcon(idx)}
              className={classNames(s.cell, cellClassName, {
                [s.preselected]: !isSunriseV2Enabled && isPreselected,
              })}
            >
              <MinusSmall className={s.minus} />
            </div>
          ),
        )}
      </div>
    );
  },
);

export const FeatureRow = withExperiments(
  withInjectedRootStore(FeatureRowComp),
);
